/** @module Profit and loss: what the cards cost, what they are worth now, what sales brought. */
import { Link } from "@tanstack/react-router";
import { ArrowDownRight, ArrowUpRight, ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";

import type { components } from "@/api/client";
import { count, money, signedMoney } from "@/lib/format";

type Profit = components["schemas"]["ProfitOut"];

export function ProfitPanel({ profit, compact = false }: { profit: Profit; compact?: boolean }) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? "de";
  const sold = Number(profit.sales_total) > 0;
  const started = profit.with_purchase > 0 || sold;
  const up = Number(profit.gain) >= 0;
  const total = profit.with_purchase + profit.without_purchase;

  return (
    <div className={`panel ${compact ? "panel-pad" : "panel-lg"} flex flex-col gap-2`}>
      <span className="t-label">{t("purchase.profit.title")}</span>
      {!started ? (
        <>
          <p className="t-sm t-muted">{t("purchase.profit.empty")}</p>
          {total > 0 ? (
            <Link to="/collection" search={{ purchase: "without" }} className="t-sm row gap-1">
              {t("purchase.profit.fill")}
              <ChevronRight className="size-4" />
            </Link>
          ) : null}
        </>
      ) : (
        <>
          {profit.with_purchase > 0 ? (
            <>
              <div className="row flex-wrap items-baseline gap-3">
                <span className={`num-xl text-[26px] ${up ? "gain" : "loss"}`}>
                  {signedMoney(profit.gain, locale)}
                </span>
                {profit.gain_percent !== null ? (
                  <span className={`badge ${up ? "badge-gain" : "badge-loss"}`}>
                    {up ? <ArrowUpRight /> : <ArrowDownRight />}
                    {up ? "+" : "−"}
                    {Math.abs(profit.gain_percent).toLocaleString(locale, {
                      maximumFractionDigits: 1,
                    })}{" "}
                    %
                  </span>
                ) : null}
              </div>
              <span className="t-sm num">
                {t("purchase.profit.basis", {
                  cost: money(profit.compared_cost, locale),
                  value: money(profit.compared_value, locale),
                })}
              </span>
              <span className="t-xs t-muted">
                {t("purchase.profit.coverage", {
                  count: profit.with_purchase,
                  cards: count(profit.with_purchase, locale),
                  total: count(total, locale),
                })}
                {profit.purchase_unvalued > 0
                  ? ` · ${t("purchase.profit.unvalued", { count: profit.purchase_unvalued })}`
                  : ""}
              </span>
            </>
          ) : null}
          {sold ? (
            <span className="t-sm">
              {t("purchase.profit.sales", { sales: money(profit.sales_total, locale) })}
              {profit.realized_count > 0 ? (
                <>
                  {" · "}
                  <span className={Number(profit.realized_gain) >= 0 ? "gain" : "loss"}>
                    {t("purchase.profit.realized", {
                      gain: signedMoney(profit.realized_gain, locale),
                      count: profit.realized_count,
                    })}
                  </span>
                </>
              ) : null}
            </span>
          ) : null}
          {profit.without_purchase > 0 ? (
            <Link to="/collection" search={{ purchase: "without" }} className="t-sm row gap-1">
              {t("purchase.profit.fill")}
              <ChevronRight className="size-4" />
            </Link>
          ) : null}
        </>
      )}
    </div>
  );
}
